import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import EventCard from "@/components/feature/EventCard";
import { categories, events, getEventById } from "@/mocks/events";

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getEventById(id) : undefined;
  const cat = item ? categories.find((c) => c.key === item.category) : undefined;

  const related = useMemo(() => {
    if (!item) return [];
    const same = events.filter((e) => e.category === item.category && e.id !== item.id);
    const others = events.filter((e) => e.category !== item.category && e.id !== item.id);
    return [...same, ...others].slice(0, 3);
  }, [item]);

  const jsonLd = useMemo(() => {
    if (!item) return [];
    const base = window.location.origin;
    return [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: base },
          { "@type": "ListItem", position: 2, name: "事件库", item: `${base}/events` },
          { "@type": "ListItem", position: 3, name: item.title, item: `${base}/event/${item.id}` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: item.title,
        description: item.summary,
        datePublished: `${item.date}T10:00:00+08:00`,
        image: item.imageUrl,
        author: { "@type": "Organization", name: "吃瓜速递" },
        publisher: { "@type": "Organization", name: "吃瓜速递" },
        inLanguage: "zh-CN",
        mainEntityOfPage: `${base}/event/${item.id}`,
      },
    ];
  }, [item]);

  if (!item) {
    return (
      <>
        <Seo
          title="事件不存在 - 吃瓜速递"
          description="未找到该吃瓜事件，返回事件库浏览更多热点。"
          path="/events"
        />
        <div className="container-x flex min-h-[60vh] flex-col items-center justify-center pt-20 text-center">
          <span className="text-4xl text-foreground-300">
            <i className="ri-emotion-sad-line"></i>
          </span>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">该事件不存在或已下线</h1>
          <Link to="/events" className="btn-primary mt-6">
            返回事件库
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo
        title={`${item.title} - 吃瓜速递`}
        description={item.summary}
        keywords={`国产吃瓜视频,${item.title},${cat?.label || "热点事件"},八卦汇总`}
        path={`/event/${item.id}`}
        jsonLd={jsonLd}
      />

      <div className="bg-foreground-950 pb-10 pt-24 md:pt-28">
        <div className="container-x">
          <nav aria-label="面包屑" className="mb-5 flex flex-wrap items-center gap-1.5 text-sm text-background-50/60">
            <Link to="/" className="transition-colors hover:text-background-50">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/events" className="transition-colors hover:text-background-50">
              事件库
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="truncate text-background-50">{item.title}</span>
          </nav>

          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
              <i className={`${cat?.icon || "ri-fire-line"} text-xs`}></i>
              {cat?.label}
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-background-50 md:text-3xl">
              {item.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-background-50/70">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {item.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {item.views} 次浏览
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-video-line"></i>
                {item.videoCount} 条视频
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-fire-fill text-accent-300"></i>
                热搜第 {item.hot} 位
              </span>
            </div>
          </div>
        </div>
      </div>

      <article className="container-x py-8 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="card overflow-hidden">
              <div className="relative h-56 sm:h-72 md:h-80">
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  title={`${item.title} 视频封面`}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-foreground-950/30">
                  <button
                    type="button"
                    className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform hover:scale-105"
                    aria-label="播放视频合集"
                  >
                    <i className="ri-play-fill text-3xl"></i>
                  </button>
                </div>
                <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-foreground-950/70 px-3 py-1 text-xs text-background-50">
                  <i className="ri-play-list-2-line"></i>
                  共 {item.videoCount} 条相关视频
                </span>
              </div>

              <div className="p-5 md:p-8">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground-800">
                  <span className="inline-flex items-center gap-1.5 text-primary-600">
                    <i className="ri-quill-pen-line"></i>
                    事件解析
                  </span>
                </div>
                <div className="mt-4 space-y-4">
                  {item.content.map((para, i) => (
                    <p key={i} className="text-sm leading-7 text-foreground-700 md:text-[15px]">
                      {para}
                    </p>
                  ))}
                </div>

                <div className="mt-7 flex flex-wrap items-center gap-2 border-t border-background-200 pt-5">
                  <span className="text-xs text-foreground-500">话题标签：</span>
                  {item.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/events?q=${encodeURIComponent(tag)}`}
                      className="chip"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
                <Link to="/events" className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700">
                  更多事件
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((r) => (
                  <EventCard key={r.id} item={r} />
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-foreground-900">关于本事件</h3>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex items-center justify-between border-b border-background-200 pb-3">
                  <span className="text-foreground-500">所属栏目</span>
                  <span className="font-medium text-foreground-800">{cat?.label}</span>
                </li>
                <li className="flex items-center justify-between border-b border-background-200 pb-3">
                  <span className="text-foreground-500">热度排名</span>
                  <span className="inline-flex items-center gap-1 font-medium text-accent-600">
                    <i className="ri-fire-fill"></i>
                    第 {item.hot} 名
                  </span>
                </li>
                <li className="flex items-center justify-between border-b border-background-200 pb-3">
                  <span className="text-foreground-500">累计浏览</span>
                  <span className="font-medium text-foreground-800">{item.views}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-foreground-500">更新日期</span>
                  <span className="font-medium text-foreground-800">{item.date}</span>
                </li>
              </ul>
            </div>

            <div className="card p-5">
              <h3 className="text-sm font-semibold text-foreground-900">往期热搜</h3>
              <ul className="mt-3 space-y-2.5">
                {events.slice(0, 4).map((e) => (
                  <li key={e.id}>
                    <Link
                      to={`/event/${e.id}`}
                      className="flex items-start gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-700"
                    >
                      <i className="ri-fire-line mt-0.5 text-accent-500"></i>
                      <span className="line-clamp-1">{e.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="card bg-primary-100/70 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-flashlight-fill text-xl"></i>
              </span>
              <h3 className="mt-3 text-sm font-semibold text-foreground-900">想看最新热点？</h3>
              <p className="mt-1.5 text-xs leading-5 text-foreground-600">
                吃瓜速递每日更新热门事件与八卦汇总，收藏本站刷新即可获取最新视频速递。
              </p>
              <Link to="/events" className="btn-primary mt-4 w-full">
                前往事件库
              </Link>
            </div>
          </aside>
        </div>
      </article>
    </>
  );
}